export const VOICE_MODEL = "grok-voice-think-fast-2.0";
export const VOICE_ENDPOINT = `wss://api.x.ai/v1/realtime?model=${VOICE_MODEL}`;

// A small initial/recovery cushion absorbs jitter without inserting gaps
// between chunks already queued. This is a candidate, pending device listening.
export const AUDIO_LEAD_SECONDS = 0.06;
export function planAudioChunk(now, queuedUntil) {
  const underrunMs = queuedUntil > 0 && queuedUntil < now
    ? (now - queuedUntil) * 1000 : 0;
  return {
    startTime: queuedUntil > now ? queuedUntil : now + AUDIO_LEAD_SECONDS,
    underrunMs,
  };
}

// In-memory timing only. Provider IDs correlate events but never leave this tracker.
// A same-response gap is evidence of queue starvation, not proof of audible crackle.
export function createPlaybackDiagnostics() {
  const responses = new Map();
  let sequence = 0;
  function begin(id, now) {
    if (!id || responses.has(id)) return;
    // Bound incomplete responses if the server omits terminal events.
    if (responses.size >= 16) responses.delete(responses.keys().next().value);
    responses.set(id, { responseNumber: ++sequence, startedAt: now,
      chunks: 0, audioMs: 0, gaps: 0, maxGapMs: 0, lastArrival: null,
      maxArrivalIntervalMs: 0, firstAudioDelayMs: null });
  }
  return {
    begin,
    chunk(id, now, durationMs, underrunMs, contextState) {
      const state = responses.get(id);
      let classification = 'unknown-response';
      if (state) {
        classification = state.chunks === 0 ? 'response-start' : 'within-response';
        if (state.chunks === 0) state.firstAudioDelayMs = Math.round(now - state.startedAt);
        if (state.lastArrival !== null) state.maxArrivalIntervalMs = Math.max(
          state.maxArrivalIntervalMs, Math.round(now - state.lastArrival));
        state.lastArrival = now;
        state.chunks += 1;
        state.audioMs += durationMs;
        if (classification === 'within-response' && underrunMs > 0) {
          state.gaps += 1;
          state.maxGapMs = Math.max(state.maxGapMs, Math.round(underrunMs));
        }
      }
      return { classification, responseNumber: state?.responseNumber ?? null,
        gapMs: Math.round(underrunMs), contextState };
    },
    finish(id, now, queuedMs) {
      const state = responses.get(id);
      if (!state) return null;
      responses.delete(id);
      return { responseNumber: state.responseNumber, chunks: state.chunks,
        audioMs: Math.round(state.audioMs), withinResponseGaps: state.gaps,
        maxGapMs: state.maxGapMs, maxArrivalIntervalMs: state.maxArrivalIntervalMs,
        firstAudioDelayMs: state.firstAudioDelayMs,
        generationMs: Math.round(now - state.startedAt),
        queuedAudioMs: Math.round(Math.max(0, queuedMs)) };
    },
    reset() { responses.clear(); sequence = 0; },
  };
}
